import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { getISOWeek, getISOWeekYear, lastWeekOfYear, pad2 } from "../lib/dates";
import "./WeekPicker.css";

function useDropdownPos(triggerRef, open) {
  const [pos, setPos] = useState(null);
  const update = useCallback(() => {
    if (!open || !triggerRef.current) { setPos(null); return; }
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 4, left: rect.left });
  }, [open, triggerRef]);
  useEffect(() => { update(); }, [update]);
  return pos;
}

export default function WeekPicker({ week, year, onChange }) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(year);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const pos = useDropdownPos(triggerRef, open);

  const now = new Date();
  const currentWeek = getISOWeek(now);
  // ISO week-YEAR: on 29.-31.12. (KW 1 of next year) or 1.-3.1. (KW 52/53 of the
  // previous year) getFullYear would highlight the wrong / no cell.
  const currentYear = getISOWeekYear(now);

  useEffect(() => {
    if (open) setViewYear(year);
  }, [open, year]);

  useEffect(() => {
    function handleClick(e) {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Fixed-position portal: page scrolling would leave it floating detached from
  // the trigger — close instead (scrolling inside the list stays allowed).
  useEffect(() => {
    if (!open) return;
    const onScroll = (e) => {
      if (dropdownRef.current && e.target instanceof Node && dropdownRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const totalWeeks = lastWeekOfYear(viewYear);
  const weeks = Array.from({ length: totalWeeks }, (_, i) => i + 1);

  const cols = 6;
  const rows = [];
  for (let i = 0; i < weeks.length; i += cols) {
    rows.push(weeks.slice(i, i + cols));
  }

  return (
    <div className="week-picker">
      <button type="button" ref={triggerRef} className="week-picker-trigger" onClick={() => setOpen((o) => !o)}>
        '{String(year).slice(-2)} KW{pad2(week)}
      </button>
      {open && pos && createPortal(
        <div className="week-picker-dropdown" ref={dropdownRef} style={{ position: "fixed", top: pos.top, left: pos.left }}>
          <div className="week-picker-header">
            <button type="button" className="week-picker-nav" onClick={() => setViewYear((y) => y - 1)}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <span className="week-picker-year">{viewYear}</span>
            <button type="button" className="week-picker-nav" onClick={() => setViewYear((y) => y + 1)}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>
          <div className="week-picker-grid">
            {rows.map((row, ri) => (
              <div key={ri} className="week-picker-row">
                {row.map((w) => {
                  const isSelected = w === week && viewYear === year;
                  const isCurrent = w === currentWeek && viewYear === currentYear;
                  return (
                    <button
                      key={w}
                      type="button"
                      className={`week-picker-cell ${isSelected ? "week-picker-cell-selected" : ""} ${isCurrent ? "week-picker-cell-current" : ""}`}
                      onClick={() => { onChange(w, viewYear); setOpen(false); }}
                    >
                      {pad2(w)}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
